import { api } from './api';
import { socketService } from './socketService';
import type { DirectMessage, Conversation } from '../store/messageStore';

export interface ConversationsResponse {
  conversations: Conversation[];
}

export interface ConversationMessagesResponse {
  messages: DirectMessage[];
  hasNextPage: boolean;
  nextCursor: string | null;
}

export interface SendMessageRequest {
  receiverId: number;
  message: string;
  messageType?: 'TEXT' | 'EMOJI' | 'GIF' | 'IMAGE';
  gifUrl?: string;
  gifId?: string;
}

export interface SendMessageResponse {
  id: string;
  message: string;
  messageType: 'TEXT' | 'EMOJI' | 'GIF' | 'IMAGE';
  gifUrl?: string;
  gifId?: string;
  sentAt: string;
  sender: {
    id: string;
    username: string;
    profilePhoto: string | null;
  };
  receiver: {
    id: string;
    username: string;
    profilePhoto: string | null;
  };
}

export interface CanMessageResponse {
  canMessage: boolean;
  reason: 'ALLOWED' | 'PREVIOUS_CONVERSATION' | 'SHARED_GROUPS' | 'GROUPS_REQUIRED' | 'SAME_USER';
  message?: string;
}

export interface HasUnreadResponse {
  hasUnread: boolean;
}

export class MessageService {
  /**
   * Get user's conversation list
   */
  static async getConversations(): Promise<ConversationsResponse> {
    const response = await api.get<{success: boolean, data: ConversationsResponse}>('/messages/conversations');
    return response.data.data;
  }

  /**
   * Get messages in a conversation with another user
   */
  static async getConversationMessages(
    userId: string,
    limit: number = 50,
    cursor?: string
  ): Promise<ConversationMessagesResponse> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (cursor) {
      params.append('cursor', cursor);
    }

    const response = await api.get<{success: boolean, data: ConversationMessagesResponse}>(
      `/messages/conversation/${userId}?${params}`
    );
    return response.data.data;
  }

  /**
   * Send a direct message via WebSocket for real-time delivery
   */
  static async sendMessage(data: SendMessageRequest): Promise<SendMessageResponse> {
    // Use socket service for real-time message delivery
    const result = await socketService.sendDirectMessage(
      data.receiverId,
      data.message,
      data.messageType || 'TEXT',
      data.gifUrl,
      data.gifId
    );
    
    // Transform socket response to match expected interface
    return {
      id: result.id,
      message: result.message,
      messageType: result.messageType,
      gifUrl: result.gifUrl,
      gifId: result.gifId,
      sentAt: result.sentAt,
      sender: result.sender,
      receiver: result.receiver
    };
  }

  /**
   * Mark conversation as read
   */
  /**
   * Upload a chat image (photo for group or direct chat).
   * Returns the URL to send as an IMAGE message. Premium required (403).
   */
  static async uploadChatImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('chatImage', file);

    const response = await api.post<{success: boolean, data: {url: string}}>(
      '/messages/upload-image',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data.data.url;
  }

  static async markConversationAsRead(userId: string): Promise<void> {
    await api.post(`/messages/mark-read/${userId}`);
  }

  /**
   * Check if user can message another user
   */
  static async canMessageUser(userId: string): Promise<CanMessageResponse> {
    const response = await api.post<{success: boolean, data: CanMessageResponse}>(`/messages/can-message/${userId}`);
    return response.data.data;
  }

  /**
   * Check messaging permission status (alias for canMessageUser)
   */
  static async checkMessagePermission(userId: string): Promise<CanMessageResponse> {
    return this.canMessageUser(userId);
  }

  /**
   * Check if user has unread messages
   */
  static async hasUnreadMessages(): Promise<HasUnreadResponse> {
    const response = await api.get<HasUnreadResponse>('/messages/has-unread');
    return response.data;
  }
}